/**
 * Executive XLSX Chat Page
 * 
 * Main page for the Executive XLSX Chat feature.
 * Allows users to upload, view, and interact with Excel files using AI.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Updated after MUI conversion
import { 
  Box, 
  Button, 
  Typography, 
  Alert, 
  IconButton, 
  Paper, 
  Chip,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  ArrowBack,
  CloudUpload,
  Description,
  Download,
  Close,
  Error as ErrorIcon
} from '@mui/icons-material';
import { UniverViewer, UniverViewerHandle } from '../components/xlsx/UniverViewer';
import { XLSXChatSidebar } from '../components/xlsx/XLSXChatSidebar';
import { v4 as uuidv4 } from 'uuid';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export const ExecutiveXLSXChatPage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const univerViewerRef = useRef<UniverViewerHandle>(null);
  
  // State
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sheetData, setSheetData] = useState<any[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCell, setSelectedCell] = useState<any>(null);
  const [selectedRange, setSelectedRange] = useState<any>(null);
  const [activeSheet, setActiveSheet] = useState<{ name: string; index: number } | null>(null);
  const [conversationId, setConversationId] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState<number>(384); // Default width
  const [isResizing, setIsResizing] = useState(false);

  /**
   * Handle AI modifications - apply incrementally when possible
   */
  const handleModifications = useCallback(async (modifications: any[]) => {
    if (!sessionId || !modifications || modifications.length === 0) return;

    try {
      console.log('🔄 Processing', modifications.length, 'modifications');

      // Check if any structural changes require full reload
      const structuralChanges = [
        'add_sheet', 'rename_sheet', 'delete_sheet', 
        'insert_rows', 'delete_rows', 'append_rows',
        'insert_columns', 'delete_columns',
        'merge_cells', 'unmerge_cells'
      ];
      const needsFullReload = modifications.some(mod => structuralChanges.includes(mod.type));

      if (needsFullReload) {
        console.log('🔄 Structural changes detected, performing full reload...');
        console.log('  Modifications that triggered reload:', modifications.map(m => m.type).join(', '));
        
        // Fetch updated data
        const dataResponse = await fetch(`${API_BASE_URL}/xlsx-chat/session/${sessionId}/data`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!dataResponse.ok) {
          throw new Error('Failed to reload session data');
        }

        const dataResponseJson = await dataResponse.json();
        console.log('✅ Sheet data reloaded:', dataResponseJson.data.length, 'sheets');
        
        // Debug: Check if data includes the new row
        const firstSheet = dataResponseJson.data[0];
        if (firstSheet) {
          console.log('  Sheet row count:', firstSheet.rows?.length || Object.keys(firstSheet.rows || {}).length);
          console.log('  Last row (100):', firstSheet.rows?.[100]);
        }
        
        // Full reload via ref
        if (univerViewerRef.current) {
          await univerViewerRef.current.reloadData(dataResponseJson.data);
          
          // IMPORTANT: After reload, apply any non-structural modifications
          // (formatting, etc.) that aren't captured in the session data reload
          const nonStructuralMods = modifications.filter(mod => !structuralChanges.includes(mod.type));
          if (nonStructuralMods.length > 0) {
            console.log('🎨 Applying', nonStructuralMods.length, 'non-structural modifications after reload...');
            // Small delay to ensure Univer has finished processing the reload
            await new Promise(resolve => setTimeout(resolve, 100));
            await univerViewerRef.current.applyModifications(nonStructuralMods);
            console.log('✅ Non-structural modifications applied');
          }
          
          // If a new sheet was created, activate it
          const addSheetMod = modifications.find(mod => mod.type === 'add_sheet');
          if (addSheetMod && addSheetMod.data?.index !== undefined) {
            console.log('📋 Activating newly created sheet at index:', addSheetMod.data.index);
            await new Promise(resolve => setTimeout(resolve, 50));
            univerViewerRef.current.activateSheet(addSheetMod.data.index);
          }
        } else {
          setSheetData(dataResponseJson.data);
        }
      } else {
        // Apply modifications incrementally
        console.log('✨ Applying incremental updates (no reload needed)');
        if (univerViewerRef.current) {
          await univerViewerRef.current.applyModifications(modifications);
        } else {
          console.warn('⚠️ UniverViewer ref not available, falling back to full reload');
          const dataResponse = await fetch(`${API_BASE_URL}/xlsx-chat/session/${sessionId}/data`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          const dataResponseJson = await dataResponse.json();
          setSheetData(dataResponseJson.data);
        }
      }

      console.log('✅ Modifications applied successfully');

    } catch (err: any) {
      console.error('❌ Modification error:', err);
      setError('Failed to apply spreadsheet changes');
    }
  }, [sessionId]);

  /**
   * Handle file upload
   */
  const handleFileUpload = useCallback(async (file: File) => {
    try {
      setIsUploading(true);
      setError(null);

      console.log('📤 Uploading file:', file.name, `(${file.size} bytes)`);

      // Validate file type
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (ext !== 'xlsx' && ext !== 'xls') {
        throw new Error('Only .xlsx and .xls files are supported');
      }

      // Validate file size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        throw new Error('File is too large. Maximum size is 50MB');
      }

      // Create conversation ID if not exists
      const convId = conversationId || `xlsx-${uuidv4()}`;
      setConversationId(convId);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('conversationId', convId);

      const response = await fetch(`${API_BASE_URL}/xlsx-chat/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(errorData.error || `Upload failed: ${response.statusText}`);
      }

      const responseData = await response.json();
      const { sessionId: newSessionId, metadata } = responseData;

      console.log('✅ Upload successful:', newSessionId);
      console.log('📊 Metadata:', metadata);

      setSessionId(newSessionId);
      setFileName(metadata.fileName);

      // Fetch sheet data
      console.log('📥 Fetching sheet data...');
      const dataResponse = await fetch(`${API_BASE_URL}/xlsx-chat/session/${newSessionId}/data`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!dataResponse.ok) {
        throw new Error('Failed to fetch session data');
      }

      const dataResponseJson = await dataResponse.json();
      console.log('✅ Sheet data received:', dataResponseJson.data.length, 'sheets');
      setSheetData(dataResponseJson.data);

    } catch (err: any) {
      console.error('❌ Upload error:', err);
      setError(err.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  }, [conversationId]);

  /**
   * Handle export
   */
  const handleExport = useCallback(async () => {
    if (!sessionId) return;

    try {
      console.log('📤 Exporting session:', sessionId);

      const response = await fetch(`${API_BASE_URL}/xlsx-chat/export/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName.replace(/\.xlsx?$/i, '_modified.xlsx');
      a.click();
      window.URL.revokeObjectURL(url);

      console.log('✅ Export complete');

    } catch (err: any) {
      console.error('❌ Export error:', err);
      setError('Failed to export file');
    }
  }, [sessionId, fileName]);

  /**
   * Handle cell click
   */
  const handleCellClick = useCallback((cell: any) => {
    setSelectedCell(cell);
    console.log('📍 Cell clicked:', cell);
  }, []);

  /**
   * Handle range select
   */
  const handleRangeSelect = useCallback((range: any) => {
    setSelectedRange(range);
    console.log('📊 Range selected:', range);
  }, []);

  /**
   * Handle active sheet change
   */
  const handleSheetChange = useCallback((sheetInfo: { name: string; index: number }) => {
    console.log('📄 Active sheet updated:', sheetInfo);
    setActiveSheet(sheetInfo);
    // Clear selection when switching sheets to avoid confusion
    setSelectedRange(null);
    setSelectedCell(null);
  }, []);

  /**
   * Handle cell edit - sync manual edits to backend
   */
  const handleCellEdit = useCallback(async (edit: { 
    sheetName: string; 
    sheetIndex: number; 
    row: number; 
    col: number; 
    value: any; 
    formula?: string 
  }) => {
    if (!sessionId) return;
    
    console.log('📝 Syncing cell edit to backend:', edit);
    
    try {
      const response = await fetch(`${API_BASE_URL}/xlsx-chat/sync-edit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          sessionId,
          sheetIndex: edit.sheetIndex,
          sheetName: edit.sheetName,
          row: edit.row,
          col: edit.col,
          value: edit.value,
          formula: edit.formula
        })
      });
      
      if (response.ok) {
        console.log('✅ Cell edit synced to backend');
      } else {
        console.error('❌ Failed to sync cell edit:', await response.text());
      }
    } catch (err) {
      console.error('❌ Error syncing cell edit:', err);
    }
  }, [sessionId]);

  /**
   * Handle close/reset
   */
  const handleClose = useCallback(async () => {
    if (sessionId) {
      try {
        await fetch(`${API_BASE_URL}/xlsx-chat/session/${sessionId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        console.log('✅ Session deleted');
      } catch (err) {
        console.error('⚠️ Error deleting session:', err);
      }
    }
    
    setSessionId(null);
    setSheetData([]);
    setFileName('');
    setSelectedCell(null);
    setSelectedRange(null);
    setActiveSheet(null);
    setConversationId('');
    setError(null);
  }, [sessionId]);

  /**
   * Handle file input change
   */
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Handle drag events
   */
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  /**
   * Handle sidebar resize
   */
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      // Constrain between 300px and 800px
      const constrainedWidth = Math.max(300, Math.min(800, newWidth));
      setSidebarWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
      {/* Top Bar */}
      <Paper 
        elevation={2} 
        sx={{ 
          px: 3, 
          py: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderRadius: 0,
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/executive-chat')}
            sx={{ textTransform: 'none' }}
          >
            Back
          </Button>
          
          <Divider orientation="vertical" flexItem />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box 
              sx={{ 
                p: 1, 
                bgcolor: 'primary.main', 
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Description sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Executive XLSX Chat
              </Typography>
              {fileName && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {fileName}
                  </Typography>
                  {sessionId && (
                    <Chip 
                      label="Active" 
                      color="success" 
                      size="small" 
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {sessionId && (
            <>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={handleExport}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Export
              </Button>
              <Button
                variant="outlined"
                startIcon={<Close />}
                onClick={handleClose}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Close
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {/* Error Banner */}
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          icon={<ErrorIcon />}
          sx={{ borderRadius: 0, mb: 0 }}
        >
          {error}
        </Alert>
      )}

      {/* Main Content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
        {/* Excel Viewer Area */}
        <Box sx={{ 
          flex: 1, 
          bgcolor: 'background.paper', 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden',
          width: sessionId ? `calc(100% - ${sidebarWidth}px)` : '100%'
        }}>
          {!sessionId ? (
            /* Upload Area */
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                p: 4
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Paper
                elevation={isDragging ? 8 : 3}
                sx={{
                  maxWidth: 600,
                  width: '100%',
                  textAlign: 'center',
                  p: 6,
                  border: 2,
                  borderStyle: 'dashed',
                  borderColor: isDragging ? 'primary.main' : 'divider',
                  bgcolor: isDragging ? 'primary.50' : 'background.paper',
                  transition: 'all 0.3s',
                  transform: isDragging ? 'scale(1.02)' : 'scale(1)',
                  cursor: 'pointer'
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'primary.main',
                      background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                      boxShadow: 4,
                      transition: 'transform 0.3s',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    {isUploading ? (
                      <CircularProgress size={60} sx={{ color: 'white' }} />
                    ) : (
                      <CloudUpload sx={{ fontSize: 60, color: 'white' }} />
                    )}
                  </Box>
                  
                  <Box>
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                      {isUploading ? 'Uploading Your File...' : 'Upload Excel Workbook'}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      {isDragging ? 'Drop your file here' : 'Drag and drop or click to browse'}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Supported formats: <strong>.xlsx, .xls</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Maximum file size: <strong>50 MB</strong>
                      </Typography>
                    </Box>
                  </Box>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={handleFileInputChange}
                    disabled={isUploading}
                  />

                  {!isUploading && (
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<Description />}
                      sx={{ 
                        mt: 2, 
                        textTransform: 'none', 
                        fontWeight: 600,
                        px: 4,
                        py: 1.5
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      Select File
                    </Button>
                  )}
                </Box>
              </Paper>
            </Box>
          ) : (
            /* Univer Viewer */
            <Box sx={{ width: '100%', height: 'calc(100vh - 120px)' }}>
              <UniverViewer
                ref={univerViewerRef}
                data={sheetData}
                onCellClick={handleCellClick}
                onRangeSelect={handleRangeSelect}
                onCellEdit={handleCellEdit}
                onSheetChange={handleSheetChange}
                readOnly={false}
                height="100%"
              />
            </Box>
          )}
        </Box>

        {/* Resize Handle */}
        {sessionId && (
          <Box
            onMouseDown={handleResizeStart}
            sx={{
              width: '4px',
              bgcolor: isResizing ? 'primary.main' : 'divider',
              cursor: 'col-resize',
              transition: 'background-color 0.2s',
              '&:hover': {
                bgcolor: 'primary.main'
              },
              zIndex: 10
            }}
          />
        )}

        {/* Chat Sidebar */}
        {sessionId && (
          <Box sx={{ 
            width: `${sidebarWidth}px`, 
            flexShrink: 0,
            overflow: 'hidden'
          }}>
            <XLSXChatSidebar
              sessionId={sessionId}
              conversationId={conversationId}
              fileName={fileName}
              selectedCell={selectedCell}
              selectedRange={selectedRange}
              activeSheet={activeSheet}
              onDataModified={handleModifications}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

